import { useState, useEffect } from "react";
import { reverse } from "ramda";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { ConstraintsCategory } from "react-migration/layouts/map/Constraints/types";
import { SingleDesignation } from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import { isDefined } from "react-migration/lib/util/isDefined";
import { Select } from "react-migration/components/Select/Select";
import { LayerTypeSelectionDetailViewProps } from "../../../types";
import { AttributeComparisonSummaryProps } from "./types";
import { AttributeComparisonSummaryUS } from "./AttributeComparisonSummaryUS";
import { AttributeComparisonSummaryUK } from "./AttributeComparisonSummaryUK";
import { DemographicsDialog } from "../DemographicsDialog";
import { DemographicsRegion } from "../types";
import { useDemographicsData } from "../hooks/useDemographicsData";
import { useSiteReportDemographicsContext } from "../DemographicsProvider";
import clsx from "clsx";

export type AttributeComparisonSummarySpec = {
  outputAreaCategoryHierarchy: ConstraintsCategory[];
  AttributeComparisonSummary: React.ComponentType<AttributeComparisonSummaryProps>;
};

export const attributeComparisonSpecMap: Record<DemographicsRegion, AttributeComparisonSummarySpec> = {
  UK: {
    outputAreaCategoryHierarchy: [
      ConstraintsCategory.UK_DEMOGRAPHICS_LSOA,
      ConstraintsCategory.UK_DEMOGRAPHICS_MSOA,
      ConstraintsCategory.UK_DEMOGRAPHICS_LAD,
      ConstraintsCategory.UK_DEMOGRAPHICS_REGION,
      ConstraintsCategory.UK_DEMOGRAPHICS_COUNTRY,
    ],
    AttributeComparisonSummary: AttributeComparisonSummaryUK,
  },
  US: {
    outputAreaCategoryHierarchy: [
      ConstraintsCategory.US_TRACT_DEMOGRAPHICS,
      ConstraintsCategory.US_COUNTY_DEMOGRAPHICS,
      ConstraintsCategory.US_STATE_DEMOGRAPHICS,
    ],
    AttributeComparisonSummary: AttributeComparisonSummaryUS,
  },
};

export function OutputAreaDetailView(props: LayerTypeSelectionDetailViewProps) {
  const { selection } = props;

  const hookData = useDemographicsData({
    designationId: selection.id,
  });

  const contextData = useSiteReportDemographicsContext();

  const {
    designation: selectedDesignation,
    superiorDesignations: intersectingDesignations,
    comparableDesignations: contextComparableDesignations,
    region,
    attributeComparisonSpec,
    loading,
  } = contextData?.targetDesignation ? contextData : hookData;

  const superiorOutputAreas = contextData?.targetDesignation
    ? contextComparableDesignations || intersectingDesignations
    : intersectingDesignations;

  if (loading || !selectedDesignation) {
    return <Loading />;
  }

  if (!attributeComparisonSpec) return null;

  return (
    <OutputAreaSummaryComparison
      region={region}
      outputArea={selectedDesignation}
      superiorOutputAreas={superiorOutputAreas}
      attributeComparisonSummarySpec={attributeComparisonSpec}
    />
  );
}

interface OutputAreaSummaryComparisonProps {
  region?: DemographicsRegion;
  outputArea: SingleDesignation;
  superiorOutputAreas: SingleDesignation[];
  attributeComparisonSummarySpec: AttributeComparisonSummarySpec;
  printable?: boolean;
  hiddenSections?: string[];
  initialComparableId?: string;
}

export function OutputAreaSummaryComparison({
  region,
  outputArea,
  superiorOutputAreas,
  attributeComparisonSummarySpec,
  printable = false,
  hiddenSections = [] as string[],
  initialComparableId,
}: OutputAreaSummaryComparisonProps) {
  const { outputAreaCategoryHierarchy, AttributeComparisonSummary } = attributeComparisonSummarySpec;

  const comparableDesignations = reverse(outputAreaCategoryHierarchy)
    .flatMap((category) => superiorOutputAreas.filter((oa) => oa.sub_category?.id === category))
    .filter(isDefined);

  const defaultId =
    printable && initialComparableId && comparableDesignations.some((o) => o.id === initialComparableId)
      ? initialComparableId
      : comparableDesignations[0]?.id;
  const [selectedId, setSelectedId] = useState<string>(defaultId);

  useEffect(() => {
    if (initialComparableId && initialComparableId !== selectedId) {
      setSelectedId(initialComparableId);
    }
  }, [initialComparableId]);

  const comparableDesignation = comparableDesignations.find((oa) => oa.id === selectedId);

  return (
    <div className="flex flex-col p-2 bg-neutral-50">
      {!!superiorOutputAreas.length && (
        <div
          className={clsx(
            "sticky top-0 left-0 z-10 bg-neutral-50 border-b border-neutral-200 mx-[-8px] mt-[-8px] p-2 flex flex-row gap-x-2 items-center",
            {
              "print:hidden hidden": printable,
            }
          )}
        >
          <label className="shrink-0">Compare to</label>
          <div className="overflow-hidden shrink grow">
            <OutputAreaSelect
              value={selectedId}
              setValue={setSelectedId}
              disabled={superiorOutputAreas.length <= 1}
              superiorOutputAreas={superiorOutputAreas}
              outputAreaCategoryHierarchy={outputAreaCategoryHierarchy}
            />
          </div>
          {region && <DemographicsDialog region={region} />}
        </div>
      )}

      <div className="flex flex-col divide-y divide-solid divide-neutral-200">
        <AttributeComparisonSummary
          outputArea={outputArea}
          comparableDesignation={comparableDesignation}
          allOutputAreas={comparableDesignations}
          printable={printable}
          hiddenSections={hiddenSections}
        />
      </div>
    </div>
  );
}

interface OutputAreaSelectProps {
  value: string;
  setValue: (v: string) => void;
  disabled: boolean;
  superiorOutputAreas: SingleDesignation[];
  outputAreaCategoryHierarchy: ConstraintsCategory[];
}

export function OutputAreaSelect({
  value,
  setValue,
  disabled,
  superiorOutputAreas,
  outputAreaCategoryHierarchy,
}: OutputAreaSelectProps) {
  const options = outputAreaCategoryHierarchy
    .flatMap((c) => superiorOutputAreas.filter((oa) => oa.sub_category?.id === c))
    .filter(isDefined)
    .reverse();

  return (
    <Select value={value} onValueChange={setValue} disabled={disabled}>
      {outputAreaCategoryHierarchy
        .flatMap((c) => superiorOutputAreas.filter((oa) => oa.sub_category?.id === c))
        .filter(isDefined)
        .reverse()
        .map((outputArea) => (
          <Select.Option key={outputArea.id} value={outputArea.id}>
            {`${outputArea.display_name || outputArea.key_code} (${outputArea.sub_category?.display_name})`}
          </Select.Option>
        ))}
    </Select>
  );
}

export const Loading = () => (
  <div className="overflow-auto bg-neutral-50">
    <div className="relative min-h-[250px] m-2 rounded-sm">
      <SkeletonLoading rows={3} showPadding={false} bgClassName="bg-neutral-50" />
    </div>
  </div>
);
